import { useAtomValue } from "jotai";
import { useState } from "react";
import { toast } from "sonner";
import { inputClass } from "@/lib/forms";
import { atoms } from "@/state";

interface ModelNotesProps {
  modelCode: string;
}

// created_at is a plain ISO string now (it was a Firestore Timestamp).
const formatTimestamp = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
};

export const ModelNotes: React.FC<ModelNotesProps> = ({ modelCode }) => {
  const notesQuery = useAtomValue(atoms.data.notesByCode(modelCode));
  const addMutation = useAtomValue(atoms.data.addNote);
  const signedIn = useAtomValue(atoms.auth.isSignedIn);
  const [draft, setDraft] = useState("");

  const notes = notesQuery.data ?? [];

  const onSubmit = async () => {
    const text = draft.trim();
    if (!text) {
      return;
    }
    try {
      await addMutation.mutateAsync({ modelCode, text });
      setDraft("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save note");
    }
  };

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        Notes
      </h2>

      {notesQuery.isLoading ? (
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Loading…</p>
      ) : notes.length === 0 ? (
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          No notes yet. {signedIn ? "Add one below." : "Sign in to add a note."}
        </p>
      ) : (
        <ul className="mt-3 flex flex-col gap-3">
          {notes.map((n) => (
            <li
              key={n.id}
              className="rounded-md border border-gray-100 bg-gray-50 px-3 py-2 text-sm dark:border-gray-800 dark:bg-gray-800/50"
            >
              <div className="whitespace-pre-wrap text-gray-800 dark:text-gray-200">{n.text}</div>
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {n.author}
                {formatTimestamp(n.created_at) && ` · ${formatTimestamp(n.created_at)}`}
              </div>
            </li>
          ))}
        </ul>
      )}

      {signedIn && (
        <div className="mt-4 flex flex-col gap-2">
          <textarea
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Anything worth remembering about this model…"
            className={inputClass}
          />
          <button
            type="button"
            disabled={!draft.trim() || addMutation.isPending}
            onClick={onSubmit}
            className="self-end rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {addMutation.isPending ? "Saving…" : "Add note"}
          </button>
        </div>
      )}
    </section>
  );
};
